// Live server discovery for the Matchmaker House.
//
// Fetches a per-region snapshot from known PartyKit rooms via their
// `onRequest` JSON endpoint, measures HTTP RTT as ping, and returns a
// `ServerEntry[]` ready to be rendered on the world map. Any room that
// fails to respond in time is dropped silently — the UI falls back to
// whatever is reachable (and to the local mock list when nothing is).

export type RegionId = 'na-east' | 'na-west' | 'eu' | 'apac' | 'sa' | 'africa';

export type ServerEntry = {
  region: RegionId;
  room: string;             // PartyKit room id (e.g. `na-east-1`)
  label: string;
  // UV position on the pixel world map, 0..1
  u: number;
  v: number;
  mapName: string;
  pingMs: number;
  virgin: number;
  chad: number;
  phase: 'warmup' | 'playing' | 'suddenDeath' | 'ended';
  mock: boolean;            // true when synthesized because fetch failed
};

// Known region entry points. PartyKit / Cloudflare DOs self-locate to
// whichever region first instantiates them, so each room prefix pins a
// shard to its expected home. Add more slots (`na-east-2`, etc) as the
// player base grows — the fetch is parallel and slow ones are dropped.
type Seed = {
  region: RegionId;
  room: string;
  label: string;
  u: number;
  v: number;
};

// UV anchors pinned to real equirectangular positions of each region's
// hub city (lon mapped to u = (lon+180)/360, lat to v = (90-lat)/180).
// Labels float above this point on the map.
const SEEDS: Seed[] = [
  { region: 'na-east', room: 'na-east-1', label: 'NA-EAST', u: 0.29, v: 0.28 },  // Washington DC
  { region: 'na-west', room: 'na-west-1', label: 'NA-WEST', u: 0.16, v: 0.29 },  // San Francisco
  { region: 'eu',      room: 'eu-1',      label: 'EU',      u: 0.52, v: 0.22 },  // Frankfurt
  { region: 'apac',    room: 'apac-1',    label: 'APAC',    u: 0.79, v: 0.49 },  // Singapore
  { region: 'sa',      room: 'sa-1',      label: 'SA',      u: 0.37, v: 0.63 },  // São Paulo
  { region: 'africa',  room: 'africa-1',  label: 'AFRICA',  u: 0.60, v: 0.51 },  // Nairobi
];

const FETCH_TIMEOUT_MS = 1500;

type SnapshotResponse = {
  virgin: number;
  chad: number;
  phase: ServerEntry['phase'];
  mapName?: string;
};

// In-session cache so the matchmaker reopens instantly. A refresh
// button on the UI can call `fetchServerList({ force: true })`.
let cached: { at: number; entries: ServerEntry[] } | null = null;
const CACHE_TTL_MS = 15_000;

export async function fetchServerList(
  partyHost: string,
  opts: { force?: boolean } = {},
): Promise<ServerEntry[]> {
  if (!opts.force && cached && Date.now() - cached.at < CACHE_TTL_MS) {
    return cached.entries;
  }

  const proto = /^localhost(:|$)|^127\./.test(partyHost) ? 'http' : 'https';

  const tasks = SEEDS.map(async (seed): Promise<ServerEntry> => {
    const url = `${proto}://${partyHost}/parties/main/${seed.room}?snapshot=1`;
    const startedAt = performance.now();
    try {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), FETCH_TIMEOUT_MS);
      const res = await fetch(url, { cache: 'no-store', signal: ctrl.signal });
      clearTimeout(timer);
      const pingMs = Math.round(performance.now() - startedAt);
      if (!res.ok) throw new Error(`bad status ${res.status}`);
      const data = (await res.json()) as SnapshotResponse;
      return {
        region: seed.region,
        room: seed.room,
        label: seed.label,
        u: seed.u,
        v: seed.v,
        mapName: data.mapName ?? deriveMapName(seed),
        pingMs,
        virgin: clampPlayers(data.virgin),
        chad: clampPlayers(data.chad),
        phase: data.phase ?? 'warmup',
        mock: false,
      };
    } catch {
      return synthesize(seed);
    }
  });

  const entries = await Promise.all(tasks);
  cached = { at: Date.now(), entries };
  return entries;
}

// Fallback mock — keeps the matchmaker populated even when offline or
// in `?solo=1`. Deterministic-ish: same layout every session, only the
// random counts differ so the map still feels alive.
export function mockServerList(): ServerEntry[] {
  return SEEDS.map(synthesize);
}

function synthesize(seed: Seed): ServerEntry {
  const phases: ServerEntry['phase'][] = ['playing', 'warmup', 'playing', 'playing', 'ended', 'warmup'];
  const basePing: Record<RegionId, [number, number]> = {
    'na-east': [40, 90],
    'na-west': [55, 120],
    eu:       [70, 150],
    apac:     [130, 230],
    sa:       [110, 200],
    africa:   [150, 260],
  };
  const [lo, hi] = basePing[seed.region];
  const rand = (min: number, max: number) => Math.floor(min + Math.random() * (max - min));
  return {
    region: seed.region,
    room: seed.room,
    label: seed.label,
    u: seed.u,
    v: seed.v,
    mapName: deriveMapName(seed),
    pingMs: rand(lo, hi),
    virgin: rand(0, 5),
    chad: rand(0, 5),
    phase: phases[Math.floor(Math.random() * phases.length)],
    mock: true,
  };
}

function deriveMapName(seed: Seed): string {
  const pool: Record<RegionId, string> = {
    'na-east': 'ALLEY-01',
    'na-west': 'MALL-RUN-02',
    eu:       'NEON-03',
    apac:     'SHIBUYA-04',
    sa:       'BEACH-05',
    africa:   'OASIS-06',
  };
  return pool[seed.region];
}

function clampPlayers(n: unknown): number {
  const v = typeof n === 'number' && Number.isFinite(n) ? Math.floor(n) : 0;
  return v < 0 ? 0 : v > 16 ? 16 : v;
}

export function pingColor(ms: number): string {
  if (ms < 80) return '#22c55e';
  if (ms < 160) return '#fde047';
  return '#ef4444';
}
